import { useEffect, useRef, useState } from "react";
import type { ActionControl, DistrictWindow, SchoolYear, Student, StudentOe } from "@/types/GlobalState";
import type { UserDetails } from "@/types";

import { useGlobalContext } from "@/components/contextProvider/ContextProvider";
import OpenEnrollmentDao from "@dao/OpenEnrollmentDao";
import SaeDao from "@dao/SaeDao";
import StudentInfoDao from "@dao/StudentInfoDao";
import UserDao from "@dao/UserDao";
import { getEpoch, getTodayWithTime } from "@utils/DateFormatter";

interface DashboardData {
    loading: boolean;
    error: Error | null;
}

type DaoResponse<T> = { data: { payload: T } } | null;

// A union of all possible dispatch actions in this hook
type DashboardDispatchAction =
    | { type: "OeSchoolYearKey"; oeSchoolYearKey: string }
    | { type: "SchoolYearDto"; schoolYearDto: SchoolYear }
    | { type: "OeDistrictWindows"; oeDistrictWindows: DistrictWindow[] }
    | { type: "SaeActionControls"; saeActionControls: ActionControl[] }
    | { type: "StudentsIc"; studentsIc: Student[] }
    | { type: "ThruDashboard"; thruDashboard: boolean }
    | { type: "StudentsFlag"; studentsFlag: { IC?: boolean; OE?: boolean } }
    | { type: "StudentsOe"; studentsOe: StudentOe[] }
    | { type: "OeActiveDistrictWindow"; oeActiveDistrictWindow: DistrictWindow }
    | { type: "OeActionControls"; oeActionControls: ActionControl[] }
    | { type: "OeSchoolYearRange"; oeSchoolYearRange: SchoolYear }
    | { type: "GuardianState"; guardianState: boolean };

type DashboardDispatch = React.Dispatch<DashboardDispatchAction>;

interface InitialKeysState {
    oeSchoolYearKey: string | null | undefined;
    schoolYearDto: SchoolYear | null | undefined;
}

interface PrimaryDataState {
    oeDistrictWindows: DistrictWindow[] | undefined;
    saeActionControls: ActionControl[] | undefined;
    studentsIc: Student[] | null | undefined;
    studentsOe: StudentOe[] | null | undefined;
    isGuardian: boolean | undefined;
    username: string | undefined;
    userDetails: UserDetails;
}

interface DependentDataState {
    oeActionControls: ActionControl[] | undefined;
    oeSchoolYearRange: SchoolYear | undefined;
}

const safePromise = <T>(promise: Promise<T> | null | undefined | false): Promise<T | null> => {
    if (!promise) return Promise.resolve(null);
    return promise.catch((err) => {
        console.warn("Non-critical query failed:", err);
        return null;
    });
};

/**
 * Step 1: Fetches and dispatches the initial school year keys required for subsequent calls.
 */
const fetchInitialKeys = async (
    state: InitialKeysState,
    signal: AbortSignal
): Promise<{
    oeSchoolYearKeyResponse: DaoResponse<string>;
    schoolYearDtoResponse: DaoResponse<SchoolYear>;
}> => {
    const needsOeSchoolYearKey = !state.oeSchoolYearKey;
    const needsSchoolYearDto = !state.schoolYearDto;
    const [oeSchoolYearKeyResponse, schoolYearDtoResponse] = await Promise.all([
        safePromise(
            needsOeSchoolYearKey && OpenEnrollmentDao<{ payload: string }>({ action: "schoolYearRead", signal })
        ),
        (needsSchoolYearDto && UserDao<{ payload: SchoolYear }>({ action: "activeSchoolYearRead", signal })) ||
            Promise.resolve(null)
    ]);
    return { oeSchoolYearKeyResponse, schoolYearDtoResponse };
};

// New helper functions to reduce complexity of fetchAndDispatchPrimaryData
const getPrimaryFetchNeeds = (
    state: PrimaryDataState
): {
    needsDistrictWindows: boolean;
    needsSaeActionControls: boolean;
    needsStudentsIc: boolean;
    needsStudentsOe: boolean;
} => ({
    needsDistrictWindows: !state.oeDistrictWindows, //
    needsSaeActionControls: !state.saeActionControls, //
    needsStudentsIc: !state.studentsIc && !!state.isGuardian,
    needsStudentsOe: !state.studentsOe //
});

const createPrimaryFetchPromises = (
    needs: ReturnType<typeof getPrimaryFetchNeeds>,
    keys: { currentOeSchoolYearKey: string | undefined; currentSchoolYearDto: SchoolYear },
    state: PrimaryDataState,
    signal: AbortSignal
): Promise<unknown>[] => {
    const { currentOeSchoolYearKey, currentSchoolYearDto } = keys;
    const { username, userDetails } = state;

    return [
        safePromise(
            needs.needsDistrictWindows &&
                !!currentOeSchoolYearKey &&
                OpenEnrollmentDao<{ payload: DistrictWindow[] }>({
                    action: "allDistrictWindowsRead",
                    schoolYearKey: currentOeSchoolYearKey,
                    signal
                })
        ),
        safePromise(
            needs.needsSaeActionControls &&
                SaeDao<{ payload: ActionControl[] }>({
                    action: "saeActionControlsRead",
                    schoolYearKey: currentSchoolYearDto.key,
                    signal
                })
        ),
        (needs.needsStudentsIc &&
            StudentInfoDao<{ payload: Student[] }>({
                action: "studentsByGuardianRead",
                username,
                signal
            })) ||
            Promise.resolve(null),
        safePromise(
            needs.needsStudentsOe &&
                !!currentOeSchoolYearKey &&
                OpenEnrollmentDao<{ payload: Student[] }>({
                    action: "studentsByParentGuidRead",
                    params: { parentGuid: userDetails.uid },
                    schoolYearKey: currentOeSchoolYearKey,
                    signal
                })
        )
    ];
};

const processPrimaryFetchResponses = (
    responses: unknown[],
    state: PrimaryDataState
): {
    districtWindowsPayload: DistrictWindow[] | undefined;
    saeActionControlsPayload: ActionControl[] | undefined;
    studentsIcPayload: Student[] | undefined;
    oeTemps: Student[] | null;
} => {
    const [districtWindowsResponse, saeActionControlsResponse, studentsIcResponse, oeTempsResponse] = responses as [
        DaoResponse<DistrictWindow[]>,
        DaoResponse<ActionControl[]>,
        DaoResponse<Student[]>,
        DaoResponse<Student[]>
    ];
    let districtWindowsPayload = state.oeDistrictWindows;

    if (districtWindowsResponse?.data) {
        const { payload } = districtWindowsResponse.data;
        payload?.sort((a, b) => (a.enrollmentRound > b.enrollmentRound ? 1 : -1));
        districtWindowsPayload = payload;
    }

    const saeActionControlsPayload = saeActionControlsResponse?.data?.payload;

    let studentsIcPayload: Student[] | undefined;
    if (studentsIcResponse?.data) {
        const { payload } = studentsIcResponse.data;
        payload?.sort((a, b) => ((getEpoch(a.birthdate) ?? 0) > (getEpoch(b.birthdate) ?? 0) ? 1 : -1));
        studentsIcPayload = payload ?? [];
    } else if (!state.isGuardian && !state.studentsIc) {
        studentsIcPayload = [];
    }

    const oeTemps: Student[] | null = oeTempsResponse?.data ? oeTempsResponse.data.payload || [] : null;
    if (oeTemps) {
        oeTemps.sort((a, b) => ((getEpoch(a.birthdate) ?? 0) > (getEpoch(b.birthdate) ?? 0) ? 1 : -1));
    }

    return { districtWindowsPayload, saeActionControlsPayload, studentsIcPayload, oeTemps };
};

/**
 * Step 2: Fetches and dispatches the primary dashboard data based on the initial keys.
 */
const fetchPrimaryData = async (
    state: PrimaryDataState,
    keys: { currentOeSchoolYearKey: string | undefined; currentSchoolYearDto: SchoolYear },
    signal: AbortSignal
): Promise<ReturnType<typeof processPrimaryFetchResponses>> => {
    const needs = getPrimaryFetchNeeds(state);
    const promises = createPrimaryFetchPromises(needs, keys, state, signal);
    const responses = await Promise.all(promises);
    return processPrimaryFetchResponses(responses, state);
};

/**
 * Step 3: Fetches and dispatches dependent data after primary data is available.
 */
// New helper function to reduce complexity of fetchAndDispatchDependentData
const processStudentDetailsResponses = (
    studentDetailsResponses: (DaoResponse<StudentOe | StudentOe[]> | null)[],
    oeTemps: Student[] | null
): StudentOe[] | undefined => {
    if (studentDetailsResponses && studentDetailsResponses.length > 0) {
        const studentDetails: StudentOe[] = studentDetailsResponses.flatMap((response, index) => {
            const studentPayload = response?.data?.payload;
            if (!studentPayload || !oeTemps) return [];

            const originalStudent = oeTemps[index];
            if (!originalStudent) return [];

            // The API can return a single object for one student's details, which is not an array.
            // This ensures we are always mapping over an array.
            const payloadAsArray = Array.isArray(studentPayload) ? studentPayload : [studentPayload];

            return payloadAsArray.map((detail) => ({
                ...detail,
                studentNumber: originalStudent.studentNumber,
                studentKey: originalStudent.key // Assuming originalStudent.key maps to studentKey in StudentOe
            }));
        });
        return studentDetails;
    }
    return undefined;
};

const findActiveDistrictWindow = (districtWindows: DistrictWindow[] | undefined): DistrictWindow | undefined => {
    if (!districtWindows) return undefined;
    const epochNow = getEpoch(getTodayWithTime());
    return districtWindows.find(
        (obj) => (getEpoch(obj.startDate) ?? 0) <= (epochNow ?? 0) && (getEpoch(obj.endDate) ?? 0) >= (epochNow ?? 0)
    );
};

const createDependentFetchPromises = (
    state: DependentDataState,
    keys: { currentOeSchoolYearKey: string | undefined },
    primaryData: { oeTemps: Student[] | null },
    activeWindow: DistrictWindow | undefined,
    signal: AbortSignal
): Promise<unknown>[] => {
    const { oeActionControls, oeSchoolYearRange } = state;
    const { currentOeSchoolYearKey } = keys;
    const { oeTemps } = primaryData;

    const oeActionControlsSyk = activeWindow ? activeWindow.schoolYearKey : currentOeSchoolYearKey;
    const needsOeActionControls = !oeActionControls && !!oeActionControlsSyk;
    const needsOeSchoolYearRange = !oeSchoolYearRange && activeWindow;

    const individualStudentDetailPromises: Promise<DaoResponse<StudentOe | StudentOe[]> | null>[] =
        Array.isArray(oeTemps) && oeTemps.length > 0
            ? oeTemps.map((student) => {
                  const daoCall = OpenEnrollmentDao<{ payload: StudentOe | StudentOe[] }>({
                      action: "studentDetailsRead",
                      key: student.key,
                      signal
                  });
                  return safePromise(daoCall);
              })
            : [];

    return [
        safePromise(
            needsOeActionControls &&
                OpenEnrollmentDao<{ payload: ActionControl[] }>({
                    action: "allActionControlsRead",
                    params: { schoolYearKey: oeActionControlsSyk },
                    signal
                })
        ),
        safePromise(
            needsOeSchoolYearRange &&
                UserDao<{ payload: SchoolYear }>({
                    action: "userSchoolYearRange",
                    schoolYearKey: activeWindow.schoolYearKey,
                    signal
                })
        ),
        Promise.all(individualStudentDetailPromises)
    ];
};

const fetchDependentData = async (
    state: DependentDataState,
    keys: { currentOeSchoolYearKey: string | undefined },
    primaryData: { districtWindowsPayload: DistrictWindow[] | undefined; oeTemps: Student[] | null },
    signal: AbortSignal
): Promise<{
    activeWindow: DistrictWindow | undefined;
    oeActionControlsPayload: ActionControl[] | undefined;
    oeSchoolYearRangePayload: SchoolYear | undefined;
    studentsOePayload: StudentOe[] | undefined;
}> => {
    const { districtWindowsPayload, oeTemps } = primaryData;

    const activeWindow = findActiveDistrictWindow(districtWindowsPayload);

    const promises = createDependentFetchPromises(state, keys, primaryData, activeWindow, signal);
    const [oeActionControlsResponse, oeSchoolYearRangeResponse, studentDetailsResponses] = (await Promise.all(
        promises
    )) as [DaoResponse<ActionControl[]>, DaoResponse<SchoolYear>, (DaoResponse<StudentOe | StudentOe[]> | null)[]];

    const oeActionControlsPayload = oeActionControlsResponse?.data?.payload;
    const oeSchoolYearRangePayload = oeSchoolYearRangeResponse?.data?.payload;
    const studentsOePayload = processStudentDetailsResponses(studentDetailsResponses, oeTemps);

    return { activeWindow, oeActionControlsPayload, oeSchoolYearRangePayload, studentsOePayload };
};

export const useDashboardData = (): DashboardData => {
    const { dispatch, state } = useGlobalContext();
    const {
        guardianState,
        oeActionControls,
        oeDistrictWindows,
        oeSchoolYearKey,
        oeSchoolYearRange,
        saeActionControls,
        schoolYearDto,
        studentsIc,
        studentsOe,
        token,
        userDetails: userDetailsRaw,
        username
    } = state || {};
    const userDetails = userDetailsRaw as UserDetails;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    // Effect to determine and dispatch guardian state once userDetails are available.
    // This separates the state-setting logic from the data-fetching logic.
    useEffect(() => {
        if (userDetails && guardianState === undefined) {
            const isGuardian = userDetails.roleDtos?.some((obj) => obj.role === "GUARDIAN") ?? false;
            (dispatch as DashboardDispatch)({ type: "GuardianState", guardianState: isGuardian });
        }
    }, [userDetails, guardianState, dispatch]);

    const isFetching = useRef(false);

    useEffect(() => {
        // Wait until token, userDetails, and guardianState are all defined before fetching.
        if (!token || !userDetails || guardianState === undefined) {
            setLoading(false);
            return;
        }

        if (isFetching.current) return;

        const controller = new AbortController();
        const { signal } = controller;
        let isActive = true;

        // Helper to satisfy complexity requirement by extracting dispatch logic
        const dispatchAllDashboardData = (
            typedDispatch: DashboardDispatch,
            init: { oeSchoolYearKey: string | undefined; schoolYearDto: SchoolYear | undefined },
            primary: ReturnType<typeof processPrimaryFetchResponses>,
            dependent: Awaited<ReturnType<typeof fetchDependentData>>
        ): void => {
            if (init.oeSchoolYearKey) typedDispatch({ type: "OeSchoolYearKey", oeSchoolYearKey: init.oeSchoolYearKey });
            if (init.schoolYearDto) typedDispatch({ type: "SchoolYearDto", schoolYearDto: init.schoolYearDto });

            if (primary.districtWindowsPayload)
                typedDispatch({ type: "OeDistrictWindows", oeDistrictWindows: primary.districtWindowsPayload });
            if (primary.saeActionControlsPayload)
                typedDispatch({ type: "SaeActionControls", saeActionControls: primary.saeActionControlsPayload });
            if (primary.studentsIcPayload) typedDispatch({ type: "StudentsIc", studentsIc: primary.studentsIcPayload });

            typedDispatch({ type: "ThruDashboard", thruDashboard: true });
            typedDispatch({ type: "StudentsFlag", studentsFlag: { IC: true } });

            if (dependent.activeWindow)
                typedDispatch({ type: "OeActiveDistrictWindow", oeActiveDistrictWindow: dependent.activeWindow });
            if (dependent.oeActionControlsPayload)
                typedDispatch({ type: "OeActionControls", oeActionControls: dependent.oeActionControlsPayload });
            if (dependent.oeSchoolYearRangePayload)
                typedDispatch({ type: "OeSchoolYearRange", oeSchoolYearRange: dependent.oeSchoolYearRangePayload });
            if (dependent.studentsOePayload)
                typedDispatch({ type: "StudentsOe", studentsOe: dependent.studentsOePayload });

            typedDispatch({ type: "StudentsFlag", studentsFlag: { OE: true } });
        };

        const fetchDashboardData = async (): Promise<void> => {
            try {
                isFetching.current = true;
                setLoading(true);

                // Step 1: Initial keys retrieval
                const { oeSchoolYearKeyResponse, schoolYearDtoResponse } = await fetchInitialKeys(
                    { oeSchoolYearKey, schoolYearDto },
                    signal
                );
                if (!isActive || signal.aborted) return;

                const currentOeSchoolYearKey = oeSchoolYearKeyResponse?.data?.payload ?? oeSchoolYearKey;
                const currentSchoolYearDto = schoolYearDtoResponse?.data?.payload ?? schoolYearDto;
                if (!currentSchoolYearDto) {
                    throw new Error("Failed to fetch active school year.");
                }
                const keys = { currentOeSchoolYearKey, currentSchoolYearDto };

                // Step 2: Primary data acquisition
                const primaryData = await fetchPrimaryData(
                    {
                        oeDistrictWindows,
                        saeActionControls,
                        studentsIc,
                        studentsOe,
                        isGuardian: guardianState,
                        username: username ?? undefined,
                        userDetails
                    },
                    keys,
                    signal
                );
                if (!isActive || signal.aborted) return;

                // Step 3: Dependent results extraction
                const dependentData = await fetchDependentData(
                    { oeActionControls, oeSchoolYearRange },
                    keys,
                    primaryData,
                    signal
                );

                // Step 4: Batch dispatch to Global State
                if (isActive && !signal.aborted) {
                    dispatchAllDashboardData(
                        dispatch,
                        {
                            oeSchoolYearKey: oeSchoolYearKeyResponse?.data?.payload,
                            schoolYearDto: schoolYearDtoResponse?.data?.payload
                        },
                        primaryData,
                        dependentData
                    );
                }
            } catch (err) {
                if ((err as Error).name !== "AbortError" && isActive) {
                    console.error("Failed to fetch dashboard data:", err);
                    setError(err as Error);
                }
            } finally {
                if (isActive) {
                    isFetching.current = false;
                    setLoading(false);
                }
            }
        };

        void fetchDashboardData();

        return (): void => {
            isActive = false;
            isFetching.current = false;
            controller.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        // This hook is designed to run once when the core user session is established.
        // It orchestrates a complex waterfall of data fetching to populate the global context.
        // The dependencies are the minimal set of stable inputs required to start the process.
        // We intentionally omit the data fields that this hook fetches (e.g., oeDistrictWindows, studentsIc)
        // because including them would cause an infinite loop of re-fetching. The internal logic
        // of the hook is responsible for checking if data already exists before fetching it again.
        dispatch,
        guardianState,
        token,
        userDetails,
        username
    ]);

    return { loading, error };
};
